/**
 * The media library takes documents as well as pictures — PDF, Office and
 * OpenDocument files, plain text and CSV (payload/src/utilities/upload-mime-types.ts).
 * Only some of those can be shown as a picture: an image previews itself, a PDF
 * previews through the cover Payload rasterises for it on upload, and everything
 * else has no image at all and previews as a sheet carrying its type and name.
 */

/** Mirrors MIME_TYPE_LABELS in payload/src/utilities/upload-mime-types.ts. */
const MEDIA_TYPE_LABELS: Record<string, string> = {
  'image/png': 'PNG',
  'image/jpeg': 'JPG',
  'image/webp': 'WEBP',
  'image/svg+xml': 'SVG',
  'application/pdf': 'PDF',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.ms-excel': 'XLS',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.ms-powerpoint': 'PPT',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
  'application/vnd.oasis.opendocument.text': 'ODT',
  'application/vnd.oasis.opendocument.spreadsheet': 'ODS',
  'application/vnd.oasis.opendocument.presentation': 'ODP',
  'text/plain': 'TXT',
  'text/csv': 'CSV',
}

/** What a page of a document looks like where we have no cover to measure — width : height. */
export const DOCUMENT_ASPECT_RATIO = 3 / 4

export const isImageMedia = (media?: Media | null): boolean =>
  Boolean(media?.mimeType?.startsWith('image/'))

export const isVideoMedia = (media?: Media | null): boolean =>
  Boolean(media?.mimeType?.startsWith('video/'))

/** The generated cover, when it was both generated and asked for by the query. */
export const mediaCover = (media?: Media | null): MediaCover | undefined =>
  typeof media?.cover === 'object' && media.cover ? media.cover : undefined

/**
 * The picture that stands in for a file: the file itself when it is one, its
 * cover when it has one, and nothing when it is a document we can't rasterise.
 */
export const mediaPreviewImage = (media?: Media | null): Media | MediaCover | undefined => {
  if (!media) { return undefined }
  if (isImageMedia(media)) { return media }
  return mediaCover(media)
}

/**
 * The shape a file is drawn in: its preview image's own, or a document sheet's
 * when there is no preview image to measure.
 */
export const mediaAspectRatio = (media?: Media | null): number => {
  const preview = mediaPreviewImage(media)
  if (!preview) { return DOCUMENT_ASPECT_RATIO }
  if (preview.width && preview.height) { return preview.width / preview.height }
  return 1
}

/**
 * 'PDF', 'DOCX'. Falls back to the extension, and then to the mime subtype, so
 * a file uploaded before a type was added to the map still says something.
 */
export const mediaTypeLabel = (media?: Media | null): string => {
  const byMimeType = media?.mimeType ? MEDIA_TYPE_LABELS[media.mimeType] : undefined
  if (byMimeType) { return byMimeType }

  const extension = media?.filename?.includes('.') ? media.filename.split('.').pop() : undefined
  if (extension) { return extension.toUpperCase() }

  return media?.mimeType?.split('/').pop()?.toUpperCase() ?? ''
}

// Editors name a file for the CMS, not for the page: the caption is the title
// worth reading, so it stands in for the filename wherever one was written.
export const mediaTitle = (media?: Media | null): string =>
  richTextToString(media?.caption) || media?.filename || ''
